/**
 * RuntimeFleet — §22 fleet-wide build-version & upgrade console.
 *
 * Lists EVERY deployed lexruntime (one row per provisioned user) with
 * its running build + skew, sourced from the operator BATCH endpoint
 * `GET /api/platform/runtimes/versions` via `useRuntimeVersions` (the
 * single source; skew is server-authoritative — never computed here).
 *
 * Per-row action: a single "Upgrade" button opens the shared
 * `FleetUpgradeAllDialog` scoped to that one runtime (the same 2-mode
 * force/soft + type-to-confirm UX as the header "Upgrade all", which
 * opens it fleet-scoped). Per the operator hand-off the actual upgrade
 * is still UNBOUND: confirming surfaces a "wiring pending" notice and
 * touches no runtime — the dialog's `onConfirm` is the seam to bind
 * later (lexserver upgrade contracts, owned by the lexserver/LexIDE
 * agent) without changing this UX.
 *
 * Feature gate: when the batch endpoint isn't live yet
 * (`available === false`) the page shows an informative notice instead
 * of a broken/empty table — it self-heals when the endpoint ships.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  Loader2,
  Monitor,
  Plug,
  RefreshCw,
  Server,
  Wrench,
  X,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useRuntimeVersions } from "@/hooks/useRuntimeVersions";
import { usePlatformApi } from "@/hooks/usePlatformApi";
import { useRuntimeContext } from "@/hooks/useRuntimeContext";
import { useGatewayRpc } from "@/hooks/useGatewayRpc";
import { VersionSkewBadge } from "@/components/shared/VersionSkewBadge";
import { BundleStateBadge } from "@/components/shared/BundleStateBadge";
import { RuntimeStateBadge } from "@/components/shared/RuntimeStateBadge";
import {
  FleetUpgradeAllDialog,
  type FleetUpgradeMode,
} from "@/components/platform/FleetUpgradeAllDialog";
import {
  RepairRuntimeDialog,
  type RepairTier,
} from "@/components/platform/RepairRuntimeDialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { sortOrgsActiveFirst } from "@/lib/org-order";
import type {
  DeployTarget,
  Organization,
  RuntimeVersionEntry,
} from "@/types/platform";
import type { OrgUser } from "@lexiun/org-admin";

/**
 * A routing row whose runtime is listed-as-deployed but not responding:
 * the probe failed (`running == null`) or the backend classified it
 * down/old-image (`skew === "unreachable"`). This is the load-bearing
 * DB↔reality drift signal — surfaced LOUDLY (red row + banner) so a
 * disaster like silently-torn-down containers can't hide behind a calm
 * grey "version unavailable" badge. NOT `indeterminate` (version-unknown
 * but possibly reachable — the masked local-dev state).
 */
function isRuntimeUnreachable(r: RuntimeVersionEntry): boolean {
  return r.skew === "unreachable" || r.running == null;
}

const DEPLOY_LABEL: Record<DeployTarget, string> = {
  docker: "Docker",
  azure: "Azure",
  "test-mock": "Test-mock",
};

declare global {
  interface Window {
    /** Test-only seam: shrink the connect deadline so e2e doesn't wait
     *  the full minute. Never set in production. */
    __lxConnectDeadlineMs?: number;
  }
}

/**
 * How long a deliberate Select may stay "connecting…" before we give up.
 * The gateway client auto-reconnects FOREVER by design (prod-verified
 * resilience for transient blips — see gateway-client `scheduleReconnect`);
 * we must NOT weaken that. Instead, for an operator-initiated selection
 * that never connects, this deadline tears down the selection
 * (`clearRuntime` ⇒ no wsUrl ⇒ the client stops) and leaves a sticky
 * "Connection failed" marker so the operator knows it never completed.
 */
const CONNECT_DEADLINE_MS = 60_000;

function connectDeadlineMs(): number {
  // DEV-gated so a production build can never be influenced by the
  // window seam even if some other script set that global.
  return import.meta.env.DEV &&
    typeof window !== "undefined" &&
    window.__lxConnectDeadlineMs
    ? window.__lxConnectDeadlineMs
    : CONNECT_DEADLINE_MS;
}

export function RuntimeFleet() {
  const api = usePlatformApi();
  // The per-row "Runtime" button wires a fleet runtime into the active
  // runtime context (the same `selectRuntime` the RuntimeSelector picker
  // uses) so the top-bar reflects it — making /runtimes the actual
  // selection hub the top-bar "Runtimes" link points at.
  const {
    target,
    selectRuntime,
    clearRuntime,
    loading: connecting,
    error: connectError,
  } = useRuntimeContext();
  // Live gateway state of the *active* runtime — used to keep the
  // per-row "Selected" affordance honest (green only when truly
  // connected; never green while the top-bar says disconnected).
  const { state: gatewayState } = useGatewayRpc();

  // Connect-deadline tracking. `pending` = a Select is in flight and
  // not yet connected; `failed` = a selection gave up after the
  // deadline (sticky marker so the operator sees it never completed,
  // survives the clearRuntime() that stops the gateway retry loop).
  const [pending, setPending] = useState<{
    orgId: string;
    userId: string;
  } | null>(null);
  const [failed, setFailed] = useState<{
    orgId: string;
    userId: string;
  } | null>(null);
  // Only a connect that happens AFTER this select (i.e. once the gateway
  // has been observed leaving "connected" for the new target) counts as
  // success. Without this, switching FROM an already-connected runtime
  // would see the prior runtime's stale "connected" and clear `pending`
  // before the new connection is even attempted — so its deadline /
  // "Connection failed" never arm. Reset on every fresh select.
  const freshConnectRef = useRef(false);

  const beginSelect = (
    orgId: string,
    userId: string,
    orgName: string,
    orgSlug: string,
  ) => {
    freshConnectRef.current = false;
    setFailed(null);
    setPending({ orgId, userId });
    void selectRuntime(orgId, userId, orgName, userId, orgSlug);
  };

  const clearSelection = () => {
    setPending(null);
    clearRuntime();
  };

  // Success cancels the deadline (and any prior failed marker) — but
  // only once we've seen a fresh (post-select) (re)connect, never a
  // leftover "connected" from the previously-selected runtime.
  useEffect(() => {
    if (!pending) return;
    if (gatewayState !== "connected") {
      freshConnectRef.current = true;
      return;
    }
    if (freshConnectRef.current) {
      setPending(null);
      setFailed(null);
    }
  }, [pending, gatewayState]);

  // External clear (e.g. the top-bar DISCONNECT button) → also clear
  // any in-flight pending. Only fires on a TRUE target-cleared event
  // (non-null → null) — checking just `!target && pending` would also
  // match the brief synchronous gap between `setPending(B)` and
  // `selectRuntime` setting target, clobbering pending before the
  // deadline could arm.
  const prevTargetRef = useRef(target);
  useEffect(() => {
    const prev = prevTargetRef.current;
    prevTargetRef.current = target;
    if (prev != null && target == null && pending) setPending(null);
  }, [target, pending]);

  // The deadline itself: if a pending selection hasn't connected within
  // the window, give up — record the sticky "connection failed" marker
  // and tear the selection down so the gateway stops its (otherwise
  // infinite, by-design) reconnect loop.
  useEffect(() => {
    if (!pending) return;
    const failedSel = pending;
    const t = setTimeout(() => {
      setFailed(failedSel);
      setPending(null);
      clearRuntime();
    }, connectDeadlineMs());
    return () => clearTimeout(t);
  }, [pending, clearRuntime]);
  const [orgFilter, setOrgFilter] = useState<string>("");
  const [orgs, setOrgs] = useState<Organization[]>([]);

  // Org list powers the name column + the per-tenant filter. Best-effort
  // and independent of the fleet data: a failure degrades to raw orgIds
  // and an "All organizations"-only filter (never blocks the fleet).
  useEffect(() => {
    let cancelled = false;
    api
      .listOrgs({ limit: 100 })
      .then((r) => {
        if (!cancelled) setOrgs(sortOrgsActiveFirst(r.data));
      })
      .catch(() => {
        if (!cancelled) setOrgs([]);
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  const orgNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of orgs) m.set(o.orgId, o.name);
    return m;
  }, [orgs]);

  // Per-user identifier lookup powers the row's primary label. Mirrors
  // the orgs loader's best-effort contract — a failure degrades to
  // raw userIds (never blocks the fleet). Fetched per visible org in
  // parallel; merged into one map keyed by userId. `email` is the
  // canonical user-facing identifier in Lexiun (no formal `slug` on
  // the User schema; `AssignInstanceDialog` + `RuntimeParameters`
  // already use email).
  const [users, setUsers] = useState<OrgUser[]>([]);
  useEffect(() => {
    if (orgs.length === 0) return;
    let cancelled = false;
    Promise.allSettled(
      orgs.map((o) => api.listOrgUsers(o.orgId, { limit: 100 })),
    ).then((results) => {
      if (cancelled) return;
      const merged: OrgUser[] = [];
      for (const r of results) {
        if (r.status === "fulfilled") merged.push(...r.value.data);
      }
      setUsers(merged);
    });
    return () => {
      cancelled = true;
    };
  }, [api, orgs]);
  const userById = useMemo(() => {
    const m = new Map<string, OrgUser>();
    for (const u of users) m.set(u.userId, u);
    return m;
  }, [users]);

  // slug mirror so beginSelect can pass o.slug to selectRuntime (the
  // top-bar reads it for a human-readable tenant label).
  const orgSlugById = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of orgs) m.set(o.orgId, o.slug);
    return m;
  }, [orgs]);

  // Empty filter ⇒ undefined ⇒ all orgs (unchanged default). Changing
  // it refetches immediately (useRuntimeVersions handles that).
  const {
    byUserId,
    targetVersion,
    available,
    error,
    initializing,
    refresh: refreshFleet,
  } = useRuntimeVersions(orgFilter || undefined);
  const [upgradeAllOpen, setUpgradeAllOpen] = useState(false);
  const [upgradeRow, setUpgradeRow] = useState<{
    userId: string;
    containerName: string;
    /** Drives the per-row dialog's localdev disclosure (docker ⇒ no Soft). */
    deployTarget?: string;
  } | null>(null);
  // Feature A — per-row Repair (reinstate).
  const [repairRow, setRepairRow] = useState<{
    userId: string;
    containerName: string;
    deployTarget?: string;
  } | null>(null);
  const [upgradeNotice, setUpgradeNotice] = useState<string | null>(null);

  const flashNotice = (msg: string) => {
    setUpgradeNotice(msg);
    setTimeout(() => setUpgradeNotice(null), 6000);
  };

  /**
   * GAP C CLOSED 2026-07-20 — docker rows are now enqueuable, so they can no
   * longer land in `skipped[]`. The old detection (which matched the retired
   * `T1 azure-only` reason) and its "use the per-user ?force curl" hint are
   * gone: the portal button IS the supported path for a local fleet now, and
   * the localdev difference is disclosed up-front in the upgrade dialog
   * instead of after the fact. Only test-mock rows are skipped today.
   */
  type SkippedRow = {
    userId: string;
    orgId: string;
    containerName: string;
    deployTarget: string;
    reason: string;
  };
  /** Rows a plan refused to act on (test-mock only, post-Gap-C). */
  const findUnupgradable = (skipped: SkippedRow[] | undefined): SkippedRow[] =>
    (skipped ?? []).filter((s) => (s.deployTarget ?? "") !== "azure" && s.deployTarget !== "docker");

  // BOTH modes are wired to `POST /api/platform/runtimes/upgrade` via the
  // two-phase plan→execute pattern. SOFT enqueues T1 idle-deferred jobs;
  // FORCE (Gap C closed 2026-06-12) enqueues with attempts=maxAttempts so the
  // worker's first tick takes the t1-forced branch and cold-boots immediately
  // (was a notice-only client stub before — operators hit the silent no-op
  // twice during the 2026-06-11/12 deploy verification). The org-filter
  // dropdown above scopes the operation: empty filter = all-orgs
  // (fleet-wide), set = scoped to that one org.
  const handleUpgradeAllConfirm = async (mode: FleetUpgradeMode) => {
    setUpgradeAllOpen(false);
    const force = mode === "force";
    const modeLabel = force ? "force" : "soft";
    const scope: "all-orgs" | { orgId: string } = orgFilter
      ? { orgId: orgFilter }
      : "all-orgs";
    const scopeLabel = orgFilter
      ? `org "${orgNameById.get(orgFilter) ?? orgFilter}"`
      : "all orgs";
    try {
      flashNotice(`Planning ${modeLabel} upgrade for ${scopeLabel}…`);
      const plan = await api.planFleetUpgrade(scope, { force });
      const { executionToken, confirmPhrase, count } = plan.data;
      if (count === 0) {
        // Phase 21.D — distinguish "docker rows filtered by the azure-
        // only route" (Gap C) from "genuinely no routing rows". The
        // first is the local-dev steady state and needs the canonical
        // per-user `?force=1` flow; the second is a real surprise.
        const unupgradable = findUnupgradable(plan.data.skipped);
        if (unupgradable.length > 0) {
          const n = unupgradable.length;
          flashNotice(
            `Nothing to upgrade in ${scopeLabel} — ${n} row${n > 1 ? "s are" : " is"} ` +
              `not an upgradable runtime (test-mock).`,
          );
          return;
        }
        flashNotice(
          `Nothing to upgrade in ${scopeLabel} — no routing rows returned by plan.`,
        );
        return;
      }
      const reason = `Manual ${modeLabel} upgrade via LexPortal UI (${scopeLabel})`;
      const exec = await api.executeFleetUpgrade(
        scope,
        executionToken,
        confirmPhrase,
        reason,
        { force },
      );
      flashNotice(
        force
          ? `Force upgrade dispatched — ${exec.data.enqueued} runtime(s) in ${scopeLabel} (${exec.data.skipped} skipped) will cold-boot now.`
          : `Soft upgrade scheduled — ${exec.data.enqueued} runtime(s) enqueued in ${scopeLabel} (${exec.data.skipped} skipped). Each will roll on its next idle moment.`,
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      flashNotice(`Upgrade failed: ${msg}`);
    }
  };

  // Per-row "Upgrade" is wired to the per-user branch of
  // `POST /api/platform/runtimes/upgrade` (shipped 2026-05-20 alongside this
  // SPA wiring — see `lexserver/src/routes/platform.ts` `RuntimeUserUpgradeSchema`).
  // Both modes call the same two-phase plan→execute pattern; the only
  // difference is the `force` flag, which the backend passes to the BullMQ
  // worker via `attempts = maxAttempts` (trips the EC2 force-roll branch on
  // first job execution → audit verb `t1-forced`). Force skips the idle-
  // gate, so it's the answer for rolling containers whose old image lacks
  // `/api/lx/idle-status` (the c828b94 catch-22).
  const handleRowUpgradeConfirm = async (mode: FleetUpgradeMode) => {
    const row = upgradeRow;
    setUpgradeRow(null);
    if (!row) return;
    const force = mode === "force";
    const label = `${row.containerName} (${force ? "force" : "soft"})`;
    try {
      flashNotice(`Planning ${force ? "force" : "soft"} upgrade for ${row.containerName}…`);
      const plan = await api.planRuntimeUpgrade(row.userId, { force });
      const { executionToken, confirmPhrase, count } = plan.data;
      if (count === 0) {
        // Distinguish "this row isn't upgradable at all" (test-mock) from
        // "row genuinely missing" — otherwise the operator sees the
        // soft-deleted/moved fallback and reaches for the wrong tool.
        // (Pre-2026-07-20 this branch also caught docker rows; they now roll.)
        if (findUnupgradable(plan.data.skipped).length > 0) {
          flashNotice(
            `${row.containerName} is not an upgradable runtime (test-mock row).`,
          );
          return;
        }
        flashNotice(
          `Nothing to upgrade for ${row.containerName} — no routing row ` +
            `found (the runtime may have been soft-deleted or moved since ` +
            `the table loaded).`,
        );
        return;
      }
      const reason = `Per-row ${force ? "force" : "soft"} upgrade via LexPortal UI (${label})`;
      const exec = await api.executeRuntimeUpgrade(
        row.userId,
        force,
        executionToken,
        confirmPhrase,
        reason,
      );
      flashNotice(
        force
          ? `Force upgrade scheduled for ${row.containerName} — ${exec.data.enqueued} job queued; worker will roll immediately (no idle gate).`
          : `Soft upgrade scheduled for ${row.containerName} — ${exec.data.enqueued} job queued; worker will roll on next idle moment.`,
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      flashNotice(`Upgrade failed for ${row.containerName}: ${msg}`);
    }
  };

  // Feature A — per-row Repair (reinstate): two-phase plan→execute, then
  // refresh the fleet so the State column reflects the recreated runtime.
  const handleRepairConfirm = async (tier: RepairTier, reason: string) => {
    const row = repairRow;
    setRepairRow(null);
    if (!row) return;
    try {
      const plan = await api.planReinstateRuntime(row.userId, tier);
      const exec = await api.executeReinstateRuntime(
        row.userId,
        tier,
        plan.data.executionToken,
        plan.data.confirmPhrase,
        reason,
      );
      const o = exec.data.outcome;
      flashNotice(
        o.status === "failed"
          ? `Repair (${o.tierApplied}) failed for ${row.containerName}: ${o.detail ?? "unknown"}`
          : `Repair done — ${row.containerName} ${o.status} (${o.tierApplied}).`,
      );
      refreshFleet();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      flashNotice(`Repair failed for ${row.containerName}: ${msg}`);
    }
  };

  const rows = Array.from(byUserId.values());
  // Rows an upgrade would actually act on (test-mock rows are never upgradable).
  // Drives the dialog's localdev disclosure + whether Soft is offerable.
  const upgradableRows = rows.filter(
    (r) => r.deployTarget === "azure" || r.deployTarget === "docker",
  );
  const unreachableRows = rows.filter(isRuntimeUnreachable);

  return (
    <div className="space-y-4" data-testid="runtime-fleet-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <Server className="h-5 w-5 text-indigo-500" /> Runtime Fleet
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Every deployed lexruntime, its running build, and skew vs the
            platform target
            {targetVersion ? (
              <>
                {" "}
                (<span className="font-mono">{targetVersion}</span>)
              </>
            ) : null}
            .
          </p>
        </div>
        <button
          type="button"
          onClick={() => setUpgradeAllOpen(true)}
          data-testid="fleet-upgrade-all"
          className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700"
        >
          <RefreshCw className="h-4 w-4" /> Upgrade all
        </button>
      </div>

      {/* Per-tenant filter — server-side `?orgId=` (DB-scoped). Always
          visible so the operator can scope even while the fleet is
          loading/gated. Inactive orgs are kept (a runtime under a
          SUSPENDED/TERMINATED org is an ops signal), de-prioritised
          + status-labelled via the shared active-first ordering. */}
      <div className="flex items-center gap-2">
        <label
          htmlFor="fleet-org-filter"
          className="text-sm text-muted-foreground"
        >
          Organization
        </label>
        <select
          id="fleet-org-filter"
          data-testid="fleet-org-filter"
          value={orgFilter}
          onChange={(e) => setOrgFilter(e.target.value)}
          className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900"
        >
          <option value="">All organizations</option>
          {orgs.map((o) => (
            <option key={o.orgId} value={o.orgId}>
              {o.status === "ACTIVE" ? o.name : `${o.name} — ${o.status}`}
            </option>
          ))}
        </select>
      </div>

      <FleetUpgradeAllDialog
        open={upgradeAllOpen}
        onOpenChange={setUpgradeAllOpen}
        onConfirm={handleUpgradeAllConfirm}
        dockerCount={upgradableRows.filter((r) => r.deployTarget === "docker").length}
        totalCount={upgradableRows.length}
      />

      {/* Same dialog component, scoped to one runtime (per-row Upgrade).
          Only one instance is ever open at a time, so the shared
          data-testids never collide. */}
      <FleetUpgradeAllDialog
        open={upgradeRow != null}
        onOpenChange={(o) => {
          if (!o) setUpgradeRow(null);
        }}
        onConfirm={handleRowUpgradeConfirm}
        runtimeLabel={upgradeRow?.containerName}
        dockerCount={upgradeRow?.deployTarget === "docker" ? 1 : 0}
        totalCount={upgradeRow ? 1 : 0}
      />

      {/* Feature A — per-row Repair (Restart / Recreate / Reset). */}
      <RepairRuntimeDialog
        open={repairRow != null}
        onOpenChange={(o) => {
          if (!o) setRepairRow(null);
        }}
        containerName={repairRow?.containerName}
        deployTarget={repairRow?.deployTarget}
        onConfirm={handleRepairConfirm}
      />

      {upgradeNotice && (
        <div
          className="rounded-md bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-300"
          data-testid="fleet-upgrade-notice"
        >
          {upgradeNotice}
        </div>
      )}

      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
          {error}
        </div>
      )}

      {connectError && (
        <div
          className="rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400"
          data-testid="fleet-connect-error"
        >
          Could not select runtime: {connectError}
        </div>
      )}

      {!initializing && available && unreachableRows.length > 0 && (
        <div
          className="flex items-start gap-3 rounded-md border border-red-300 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/40"
          data-testid="fleet-health-warning"
          role="alert"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600 dark:text-red-400" />
          <div className="text-sm">
            <p className="font-semibold text-red-800 dark:text-red-300">
              {unreachableRows.length} of {rows.length} runtime
              {rows.length === 1 ? "" : "s"} not responding
            </p>
            <p className="mt-0.5 text-red-700 dark:text-red-400">
              These are listed as deployed but their containers did not
              answer a version probe — the routing DB may be out of sync
              with the actual containers (recreate / restore them).
            </p>
          </div>
        </div>
      )}

      {initializing ? (
        // First poll still in flight — must NOT show the "not available"
        // notice yet (that would flash a false claim before we know).
        <p className="py-12 text-center text-sm text-slate-400" data-testid="fleet-loading">
          Loading fleet…
        </p>
      ) : !available ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Fleet version reporting not available
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground" data-testid="fleet-unavailable">
            The operator batch endpoint{" "}
            <code className="font-mono">GET /api/platform/runtimes/versions</code> is not live
            yet, unreachable, or your session has expired. This page populates
            automatically once it is reachable — no action needed.
          </CardContent>
        </Card>
      ) : rows.length === 0 ? (
        <p className="py-12 text-center text-sm text-slate-400" data-testid="fleet-empty">
          {error
            ? "Could not load the fleet — see the error above."
            : orgFilter
              ? "No deployed runtimes for the selected organization."
              : "No deployed runtimes reported."}
        </p>
      ) : (
        <Card>
          <CardContent className="overflow-x-auto p-0">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 dark:border-slate-700 dark:text-slate-400">
                  <th className="px-4 py-2.5">Container</th>
                  <th className="px-4 py-2.5">State</th>
                  <th className="px-4 py-2.5">User / Org</th>
                  <th className="px-4 py-2.5">Deploy</th>
                  <th className="px-4 py-2.5">Running version</th>
                  <th className="px-4 py-2.5">Built</th>
                  <th className="px-4 py-2.5">Lexruntime</th>
                  <th className="px-4 py-2.5">Lextools</th>
                  <th className="px-4 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const down = isRuntimeUnreachable(r);
                  const isActive =
                    target?.userId === r.userId && target?.orgId === r.orgId;
                  const isFailed =
                    failed?.userId === r.userId && failed?.orgId === r.orgId;
                  const selConnected = isActive && gatewayState === "connected";
                  // Commit B of LexPortal lextools-surfaces miniplan —
                  // surface a contextual hint on the Upgrade button when
                  // the runtime's lextools bundle differs from the active
                  // platform version. The Upgrade button restarts the
                  // container, which re-reads `extensions/current` on
                  // boot and adopts the active bundle (idle-restart's
                  // T1-forced equivalent reachable via the existing
                  // FleetUpgradeAllDialog scoped to this one runtime).
                  const lextoolsDiffers =
                    r.bundleTarget != null &&
                    r.bundlePending == null &&
                    r.running?.bundle != null &&
                    r.running.bundle.tag !== r.bundleTarget.tag;
                  return (
                    <tr
                      key={r.userId}
                      className={
                        down
                          ? "border-b border-l-4 border-slate-100 border-l-red-500 bg-red-50/70 last:border-b-0 dark:border-slate-800 dark:border-l-red-500 dark:bg-red-950/40"
                          : r.deployTarget === "test-mock"
                            ? "border-b border-slate-100 opacity-60 last:border-0 dark:border-slate-800"
                            : "border-b border-slate-100 last:border-0 dark:border-slate-800"
                      }
                      data-testid={`fleet-row-${r.userId}`}
                      data-unreachable={down ? "true" : undefined}
                    >
                      <td className="px-4 py-2.5 font-mono text-xs">{r.containerName}</td>
                      <td className="px-4 py-2.5">
                        <RuntimeStateBadge state={r.state} />
                      </td>
                      <td className="px-4 py-2.5 text-xs">
                        {/* Primary line: user email (the Lexiun canonical
                            user slug — `User.email`; matches the
                            pattern used by AssignInstanceDialog +
                            RuntimeParameters). Falls back to raw
                            userId when the users batch hasn't yet
                            loaded or the user is absent (best-effort,
                            never blocks the fleet). Sub-line: org name. */}
                        <span className="font-medium">
                          {userById.get(r.userId)?.email ?? r.userId}
                        </span>
                        <span className="block text-[10px] text-slate-500 dark:text-slate-400">
                          {orgNameById.get(r.orgId) ?? r.orgId}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-xs">
                        {r.deployTarget ? (
                          <>
                            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                              {DEPLOY_LABEL[r.deployTarget] ?? r.deployTarget}
                            </span>
                            {/* Which ROLLOUT applies — production rolls onto a
                                published tag; a local row is recreated on the
                                current lexruntime:local. Stated per row so the
                                mode is never invisible (ruling 2026-07-20). */}
                            {r.deployTarget === "azure" || r.deployTarget === "docker" ? (
                              <span
                                data-testid={`roll-mode-${r.userId}`}
                                title={
                                  r.deployTarget === "docker"
                                    ? "Local build: an upgrade recreates this container on the current lexruntime:local image. No published tag, so it cannot be idle-deferred."
                                    : "Production: an upgrade rolls this runtime onto the published image tag, and can be deferred to its idle moment."
                                }
                                className="mt-0.5 block text-[10px] text-slate-500 dark:text-slate-400"
                              >
                                {r.deployTarget === "docker" ? "local recreate" : "ACA roll"}
                              </span>
                            ) : null}
                          </>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-xs">
                        {down ? (
                          <span className="font-sans font-medium text-red-600 dark:text-red-400">
                            — not responding
                          </span>
                        ) : (
                          (r.running?.version ?? "unknown")
                        )}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-xs">
                        {down ? "—" : (r.running?.builtAt ?? "unknown")}
                      </td>
                      <td className="px-4 py-2.5">
                        {down ? (
                          <span
                            data-testid={`fleet-unreachable-${r.userId}`}
                            className="inline-flex items-center gap-1 rounded-md bg-red-100 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-red-700 dark:bg-red-900/50 dark:text-red-300"
                          >
                            <AlertTriangle className="h-3 w-3" /> Unreachable
                          </span>
                        ) : (
                          <VersionSkewBadge
                            skew={r.skew}
                            targetVersion={targetVersion}
                          />
                        )}
                      </td>
                      {/* Step 7 of ai-docs/lextools-idle-restart-activation-plan.md
                          — per-row lextools bundle state. Up-to-date / Pending
                          T1-idle / Behind / Indeterminate / —. */}
                      <td className="px-4 py-2.5">
                        <BundleStateBadge
                          bundleTarget={r.bundleTarget}
                          bundlePending={r.bundlePending}
                          runningBundle={r.running?.bundle ?? null}
                        />
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex justify-end gap-1.5">
                          {isActive ? (
                            <>
                              {selConnected ? (
                                // Connected → the cell becomes a direct
                                // link to the Runtime page (clicking
                                // navigates to "/runtime"). This is the only
                                // entry point to the Runtime page from
                                // the fleet (the sidebar shortcut is
                                // intentionally removed; "/" now lands
                                // on Organizations).
                                <Link
                                  to="/runtime"
                                  data-testid={`fleet-select-${r.userId}`}
                                  title="Open the Runtime page for this connected runtime"
                                  className="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 dark:hover:bg-emerald-900/50"
                                >
                                  <Monitor className="h-3 w-3" /> Runtime
                                </Link>
                              ) : (
                                <span
                                  data-testid={`fleet-select-${r.userId}`}
                                  className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-slate-50 px-2 py-1 text-xs font-medium text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                                >
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                  Connecting…
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={clearSelection}
                                data-testid={`fleet-clear-${r.userId}`}
                                title="Disconnect this runtime (stops the reconnect loop)"
                                className="rounded-md border border-slate-300 p-1 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </>
                          ) : isFailed ? (
                            <>
                              <span
                                data-testid={`fleet-connect-failed-${r.userId}`}
                                className="inline-flex items-center gap-1 rounded-md border border-red-300 bg-red-50 px-2 py-1 text-xs font-medium text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
                                title="Gave up after the connect deadline — the runtime never responded; the reconnect loop was stopped."
                              >
                                <AlertTriangle className="h-3 w-3" /> Connection
                                failed
                              </span>
                              <button
                                type="button"
                                onClick={() =>
                                  beginSelect(
                                    r.orgId,
                                    r.userId,
                                    orgNameById.get(r.orgId) ?? r.orgId,
                                    orgSlugById.get(r.orgId) ?? r.orgId,
                                  )
                                }
                                disabled={connecting}
                                data-testid={`fleet-retry-${r.userId}`}
                                title="Retry connecting to this runtime"
                                className="inline-flex items-center gap-1 rounded-md border border-indigo-300 px-2 py-1 text-xs font-medium text-indigo-700 transition-colors hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-indigo-700 dark:text-indigo-300 dark:hover:bg-indigo-900/30"
                              >
                                <RefreshCw className="h-3 w-3" /> Retry
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              onClick={() =>
                                beginSelect(
                                  r.orgId,
                                  r.userId,
                                  orgNameById.get(r.orgId) ?? r.orgId,
                                  orgSlugById.get(r.orgId) ?? r.orgId,
                                )
                              }
                              disabled={connecting}
                              data-testid={`fleet-select-${r.userId}`}
                              title={
                                down
                                  ? "Runtime not responding — connecting will likely fail"
                                  : "Connect to this runtime (sets it as the active context — once connected, the cell becomes a link to the Runtime page)"
                              }
                              className="inline-flex items-center gap-1 rounded-md border border-indigo-300 px-2 py-1 text-xs font-medium text-indigo-700 transition-colors hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-indigo-700 dark:text-indigo-300 dark:hover:bg-indigo-900/30"
                            >
                              {connecting ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Plug className="h-3 w-3" />
                              )}
                              {connecting ? "Connecting…" : "Connect"}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() =>
                              setUpgradeRow({
                                userId: r.userId,
                                containerName: r.containerName,
                                deployTarget: r.deployTarget,
                              })
                            }
                            data-testid={`fleet-upgrade-${r.userId}`}
                            data-lextools-differs={lextoolsDiffers ? "true" : undefined}
                            title={
                              lextoolsDiffers
                                ? "Upgrade this runtime — restart to adopt the active lextools bundle (or change the lexruntime image). Force/soft chosen in the dialog."
                                : "Upgrade this runtime (choose force / soft in the dialog)"
                            }
                            className={
                              lextoolsDiffers
                                ? "inline-flex items-center gap-1 rounded-md border border-amber-400 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800 ring-1 ring-amber-300 transition-colors hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200 dark:ring-amber-800 dark:hover:bg-amber-900/50"
                                : "inline-flex items-center gap-1 rounded-md border border-indigo-300 px-2 py-1 text-xs font-medium text-indigo-700 transition-colors hover:bg-indigo-50 dark:border-indigo-700 dark:text-indigo-300 dark:hover:bg-indigo-900/30"
                            }
                          >
                            <RefreshCw className="h-3 w-3" /> Upgrade
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setRepairRow({
                                userId: r.userId,
                                containerName: r.containerName,
                                deployTarget: r.deployTarget,
                              })
                            }
                            data-testid={`fleet-repair-${r.userId}`}
                            title="Repair this runtime (Restart / Recreate / Reset) — a data-safe recreate over the same volume; recovers a wedged / crashed / won’t-start runtime"
                            className="inline-flex items-center gap-1 rounded-md border border-amber-400 bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-800 transition-colors hover:bg-amber-100 dark:border-amber-600 dark:bg-amber-900/30 dark:text-amber-200 dark:hover:bg-amber-900/50"
                          >
                            <Wrench className="h-3 w-3" /> Repair
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
